import { useState } from 'react';

import { createOptionGroup, updateOptionGroup } from '../../api/business/endpoints';
import type { OptionGroup, TextChanges } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { LocalisedTextField } from '../../components/LocalisedTextField';
import { textChanges, textsOf } from '../../components/localisedText';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useT } from '../../i18n/LanguageContext';

type OptionGroupDialogProps = {
  companyId: string;
  /** null creates a group; a group renames it. */
  group: OptionGroup | null;
  onClose: () => void;
};

/**
 * A group's name in every content language — unique in the company per language, in any case; the API says so
 * when it is not.
 */
export const OptionGroupDialog = ({ companyId, group, onClose }: OptionGroupDialogProps) => {
  const t = useT();
  const { languages, available } = useContentLanguages();
  const original = group ? textsOf(group.translations, 'name', languages) : null;
  const [names, setNames] = useState<Record<string, string>>(original ?? {});

  const mutation = useAppMutation(
    (changes: TextChanges) =>
      group
        ? updateOptionGroup(companyId, group.id, { names: changes })
        : createOptionGroup(companyId, changes),
    ['option-groups', 'product'],
    onClose,
  );

  return (
    <FormDialog
      title={group ? t('optionGroups.rename') : t('optionGroups.create')}
      onClose={onClose}
      onSubmit={() => mutation.mutate(textChanges(names, original, languages))}
      submitLabel={group ? t('common.save') : t('common.create')}
      isBusy={mutation.isPending || (names[languages[0]] ?? '').trim() === ''}
      error={mutation.error}
      maxWidth="xs"
    >
      <LocalisedTextField
        autoFocus
        label={t('optionGroups.name')}
        languages={languages}
        available={available}
        values={names}
        onChange={setNames}
        maxLength={80}
      />
    </FormDialog>
  );
};
